import { AUTH_PROVIDER } from '@/constants/auth';
import { signIn } from 'next-auth/react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, FormControlLabel, IconButton, Tooltip } from '@/primitives';
import { ACTIVE_SALE_LISTINGS_TOGGLE_LABEL } from '../../constants';
import { IOSSwitch } from './basicSearchDialogUtils';

interface MyHerdToggleProps {
  isAuthenticated: boolean;
  hasMemberAssociations: boolean;
  myAnimals: boolean;
  signInCallbackUrl: string | undefined;
  paginationPageSize: number;
  setMyAnimals: (value: boolean) => void;
  setPaginationModel: (model: { page: number; pageSize: number }) => void;
  setMyHerdLinkingInfoOpen: (value: boolean) => void;
}

export function MyHerdToggle({
  isAuthenticated,
  hasMemberAssociations,
  myAnimals,
  signInCallbackUrl,
  paginationPageSize,
  setMyAnimals,
  setPaginationModel,
  setMyHerdLinkingInfoOpen,
}: MyHerdToggleProps) {
  const interactiveRow = (
    <Box
      onClick={() => {
        if (!isAuthenticated) {
          void signIn(AUTH_PROVIDER, {
            callbackUrl: signInCallbackUrl,
          });
        }
      }}
      onKeyDown={(event) => {
        if (!isAuthenticated && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          void signIn(AUTH_PROVIDER, {
            callbackUrl: signInCallbackUrl,
          });
        }
      }}
      role={!isAuthenticated ? 'button' : undefined}
      tabIndex={!isAuthenticated ? 0 : undefined}
      aria-label={
        !isAuthenticated ? 'Sign in to enable My Herd filtering.' : undefined
      }
      sx={{
        display: 'flex',
        alignItems: 'center',
        cursor: !isAuthenticated ? 'pointer' : undefined,
        label: { gap: '4px', m: 0 },
        '.MuiSwitch-root': { order: 1 },
      }}
    >
      <FormControlLabel
        control={
          <IOSSwitch
            checked={
              isAuthenticated && hasMemberAssociations ? myAnimals : false
            }
            disabled={!isAuthenticated || !hasMemberAssociations}
            onChange={(event) => {
              if (!isAuthenticated || !hasMemberAssociations) return;
              setMyAnimals(event.target.checked);
              setPaginationModel({
                page: 0,
                pageSize: paginationPageSize,
              });
            }}
          />
        }
        label={
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {!isAuthenticated && (
              <LockOutlinedIcon
                aria-hidden
                sx={{ fontSize: 18, color: 'text.secondary' }}
              />
            )}
            {isAuthenticated && !hasMemberAssociations && (
              <Tooltip title="How to link My Herd" placement="top">
                <IconButton
                  size="small"
                  aria-label="How to link My Herd"
                  data-dd-action-name="Basic Search: My Herd Linking Info"
                  onClick={(event) => {
                    event.stopPropagation();
                    setMyHerdLinkingInfoOpen(true);
                  }}
                  sx={{
                    p: 0.25,
                    color: 'text.secondary',
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            My Herd
          </Box>
        }
      />
    </Box>
  );

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'center',
        minWidth: 0,
      }}
    >
      {!isAuthenticated ? (
        <Tooltip title="Sign in to enable My Herd filtering." placement="top">
          <Box component="span" sx={{ display: 'inline-block' }}>
            {interactiveRow}
          </Box>
        </Tooltip>
      ) : (
        interactiveRow
      )}
    </Box>
  );
}

interface OnSaleToggleProps {
  animalsOnSaleOnly: boolean;
  paginationPageSize: number;
  setAnimalsOnSaleOnly: (value: boolean) => void;
  setPaginationModel: (model: { page: number; pageSize: number }) => void;
}

export function OnSaleToggle({
  animalsOnSaleOnly,
  paginationPageSize,
  setAnimalsOnSaleOnly,
  setPaginationModel,
}: OnSaleToggleProps) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'center',
        minWidth: 0,
        label: { gap: '4px', m: 0 },
        '.MuiSwitch-root': { order: 1 },
      }}
    >
      <Tooltip
        title="Show only animals with an active sale or private treaty listing."
        placement="top"
      >
        <FormControlLabel
          control={
            <IOSSwitch
              data-dd-action-name="Basic Search: Active Sale Listings Toggle"
              checked={animalsOnSaleOnly}
              onChange={(event) => {
                setAnimalsOnSaleOnly(event.target.checked);
                setPaginationModel({
                  page: 0,
                  pageSize: paginationPageSize,
                });
              }}
            />
          }
          label={
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {ACTIVE_SALE_LISTINGS_TOGGLE_LABEL}
            </Box>
          }
        />
      </Tooltip>
    </Box>
  );
}
